import axios, { type AxiosError } from 'axios';

import { getVesrTokenSync } from '@/app/vesr/services/vesr-auth';
import { installReauth } from '@/session/reauth';

import {
	BOTAPI,
	COMMANDAPI,
	COREAPI,
	DATAAPI,
	FEEDBACKAPI,
	getUserData,
	LMSAPI,
	MEDIAAPI,
	QUERYAPI,
	VESRAPI,
} from '../constants/global';

const addResponseInterceptors = (instance: ReturnType<typeof axios.create>) => {
	instance.interceptors.response.use(
		(response) => response,
		(error: AxiosError) => {
			if (error.response?.status === 403) {
				console.warn('[API] Permission denied (403):', error.config?.url);
			}
			return Promise.reject(error);
		}
	);
};

/**
 * A core-token client. A 401 here used to mean "log out, now" — which threw an actively
 * working user out the moment the JWT's `exp` passed. `installReauth` attaches the bearer,
 * and on a 401 refreshes once (or adopts a rotation another tab made) and replays; only a
 * refresh the server itself rejects ends the session. See `src/session/`.
 */
const createApiClient = (baseURL: string | undefined) => {
	const instance = axios.create({ baseURL });

	installReauth(instance);
	addResponseInterceptors(instance);

	return instance;
};

const createVesrApiClient = () => {
	const instance = axios.create({ baseURL: VESRAPI });

	instance.interceptors.request.use((config) => {
		const token = getVesrTokenSync();
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	});

	// VESR uses a separate token acquired asynchronously after login. A 401 here
	// means the VESR token isn't ready/valid yet — it must NOT tear down the core
	// session (mirrors feedbackApi). VESR layer handles re-acquire + retry.
	addResponseInterceptors(instance);

	return instance;
};

export const coreApi = createApiClient(COREAPI);
export const queryApi = createApiClient(QUERYAPI);
export const commandApi = createApiClient(COMMANDAPI);
export const dataApi = createApiClient(DATAAPI);
export const vesrApi = createVesrApiClient();

/**
 * api.vesr with the CORE JWT, never a VESR-minted token. For routes that authorize on the caller's
 * core UserId (team activity: an owner may read only `ownerCoreUserId` = their own id), which a
 * minted token does not carry. Like `feedbackApi`, a 401 here must not end the core session.
 */
const createVesrCoreJwtApiClient = () => {
	const instance = axios.create({ baseURL: VESRAPI });

	instance.interceptors.request.use((config) => {
		const token = getUserData().token;
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	});

	addResponseInterceptors(instance);

	return instance;
};

export const vesrCoreJwtApi = createVesrCoreJwtApiClient();

// Feedback API — separate service, no 401→logout interceptor to avoid
// clearing the user's session when the feedback service is unavailable
const createFeedbackApiClient = () => {
	const instance = axios.create({ baseURL: FEEDBACKAPI });

	instance.interceptors.request.use((config) => {
		const token = getUserData().token;
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	});

	// Ensure response data is always parsed JSON — the feedback service sometimes
	// returns JSON with a Content-Type that prevents axios auto-parsing
	instance.interceptors.response.use((response) => {
		if (typeof response.data === 'string' && response.data) {
			try {
				response.data = JSON.parse(response.data);
			} catch {
				// Not JSON — leave as string
			}
		}
		return response;
	});

	// No 401 logout interceptor — feedback service errors should never
	// log the user out of the main application
	return instance;
};

export const feedbackApi = createFeedbackApiClient();

/**
 * api.bot — Service Registry (Super Admin). It validates the core JWT with its own copy of
 * `Jwt:Key`, and when that is missing or wrong it fails closed with a 401 while the core session is
 * perfectly valid. So, like `feedbackApi`, this client attaches the core token itself and a 401
 * here must never reach `installReauth` or log the operator out — the page explains it instead.
 * Responses are bare JSON (no `{ isSuccessful, data }` envelope); errors are ProblemDetails with
 * an `errorCode`.
 */
const createBotApiClient = () => {
	const instance = axios.create({ baseURL: BOTAPI });

	instance.interceptors.request.use((config) => {
		const token = getUserData().token;
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	});

	addResponseInterceptors(instance);

	return instance;
};

export const botApi = createBotApiClient();

/** Whether this build knows where api.bot is (`REACT_APP_BASE_BOT` is baked at build time). */
export const isBotApiConfigured = (): boolean => Boolean(BOTAPI);

/**
 * Media store — the external byte store behind the Media Gallery.
 *
 * Different in kind from every client above: those are all iElect's own microservices behind the
 * gateway, whereas this is a genuinely third-party file store (eTraffica). Two consequences:
 *
 *  - **No `Authorization` header.** Forwarding our core JWT to an outside service would leak a
 *    credential that grants access to election data. The store is anonymous by design — its
 *    OpenAPI document declares no security schemes at all.
 *  - **No 401 logout.** Same reasoning as `feedbackApi` and `vesrApi`, different cause: an auth
 *    failure at an external store must never tear down the operator's iElect session.
 *
 * The base URL is an env var precisely so the store is swappable. Nothing above this client knows
 * which store is behind it; the metadata of record lives in api.core, so replacing eTraffica is a
 * one-line change here plus a CSP entry.
 */
const createMediaApiClient = () => {
	const instance = axios.create({ baseURL: MEDIAAPI });

	addResponseInterceptors(instance);

	return instance;
};

export const mediaApi = createMediaApiClient();

/**
 * Whether uploads can run at all in this build.
 *
 * Env vars are baked at build time, so a missing `REACT_APP_BASE_MEDIA` cannot be fixed at
 * runtime — the UI has to say so rather than fail on submit with a confusing network error.
 */
export const isMediaStoreConfigured = (): boolean => Boolean(MEDIAAPI);

/**
 * Learning service (api.lms) — course catalogue authoring for iElect Academy.
 *
 * It accepts the api.core JWT, so the bearer is attached — but `installReauth` is deliberately NOT
 * installed. A 401 from a separate service says nothing about the core session: refreshing on it
 * would rotate the operator's token on every LMS rejection, and a refresh failure would end a
 * session that is working everywhere else. The page renders the 401 instead (mirrors feedbackApi).
 */
const createLmsApiClient = () => {
	const instance = axios.create({ baseURL: LMSAPI });

	instance.interceptors.request.use((config) => {
		const token = getUserData().token;
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	});

	addResponseInterceptors(instance);

	return instance;
};

export const lmsApi = createLmsApiClient();

/** Env vars are baked at build time — a build without `REACT_APP_BASE_LMS` must say so. */
export const isLmsConfigured = (): boolean => Boolean(LMSAPI);
